import type {ReactElement} from 'react';
import React from 'react';
import {ControlledList} from '@Components/controlled-list';
import {Panel, PanelHeader} from '@Components/panel';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {onOpenReplaceMedia} from '@Components/poster-editor/library/poster-editor-open-modals';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {AddItemThumbnail} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-thumbnail';
import styles from '@Components/poster-editor/components/poster-editing-side-panel/components/audio-item-controls/audio-item-controls.module.scss';
import {ReplaceMediaControlsButtons} from '@Components/replace-media-controls';
import type {ItemData} from '@PosterWhiteboard/page/add-item.class';
import type {ImageSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';

interface ReplaceMediaPanelProps {
  panelId: string;
}

function ReplaceMediaPanel({...props}: ReplaceMediaPanelProps): ReactElement {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const onClose = useDefaultPanelClose(props.panelId);
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();

  const onReplace = (itemToReplaceWith: ItemData): void => {
    if (activeItem && (activeItem.isImage() || activeItem.isVideo())) {
      void currentPage?.items.replaceItems.replaceMedia(itemToReplaceWith, activeItem);
    }
  };

  const onReplaceForMediaSlide = (type: ReplaceMediaControlsButtons): void => {
    const activeSlide = activeItem?.isSlideshow() && activeItem.getSelectedSlide().isMediaSlide() ? (activeItem.getSelectedSlide() as ImageSlideItem | VideoSlideItem) : undefined;

    if (!activeSlide || !activeItem?.isSlideshow()) {
      return;
    }

    onClose();
    onOpenReplaceMedia(type, activeSlide, (itemData: ItemData): void => {
      void currentPage?.items.replaceItems.replaceMediaSlide(itemData, activeSlide, activeItem);
    });
  };

  const onReplaceOption = (type: ReplaceMediaControlsButtons): void => {
    if (activeItem?.isSlideshow()) {
      onReplaceForMediaSlide(type);
      return;
    }

    if (activeItem && !(activeItem.isImage() || activeItem.isVideo())) {
      return;
    }
    if (activeItem) {
      onClose();
      onOpenReplaceMedia(type, activeItem, (itemData: ItemData): void => {
        onReplace(itemData);
      });
    }
  };

  const getReplaceOptions = (): ControlledListItemType[] => {
    return [
      {
        id: 'replace-with-uploads',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_myuploads_capitalized'),
        subText: window.i18next.t('pmwjs_replace_with_uploads_media'),
        onClick: (): void => {
          onReplaceOption(ReplaceMediaControlsButtons.MY_UPLOADS);
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail className={styles.thumbnailWidth} icon="icon-upload-1" />,
        },
      },
      {
        id: 'replace-with-stock',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_media'),
        subText: window.i18next.t('pmwjs_replace_with_stock_media'),
        onClick: (): void => {
          onReplaceOption(ReplaceMediaControlsButtons.EXPLORE_MEDIA);
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail className={styles.thumbnailWidth} iconClassName={styles.icon} icon="icon-media" />,
        },
      },
    ];
  };

  return (
    <Panel
      panelHeader={<PanelHeader title={window.i18next.t('pmwjs_replace')} onClose={onClose} />}
      panelContent={<ControlledList className="spacing-m-0 spacing-p-4" items={getReplaceOptions()} />}
    />
  );
}

export default ReplaceMediaPanel;
